import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { supabase } from '@/config/supabase';
import { appEvents } from '@/lib/app-events';
import { sessionState, useIsSessionExpired } from '@/lib/session-state';

/**
 * The session-expired strip's state — ROUTE_INVENTORY Part 1, G7.
 *
 * The `session-expired` event (a background request's token refresh failed)
 * flips `sessionState`, which every polling hook reads through
 * `useIsSessionExpired()` to pause — there is no point sending requests that
 * will all 401.
 *
 * - **Sign in again** captures the current path into `?return=`, clears the
 *   flag so the strip goes immediately, signs out LOCALLY (the stale token
 *   would otherwise still read as authenticated and `useAuthRedirect` would
 *   bounce the user straight back), and navigates to the sign-in form.
 * - Supabase's `SIGNED_IN` / `TOKEN_REFRESHED` clears it with no user action.
 * - **Dismissing** lasts until the next `session-expired`: a user who hides
 *   it and then tries something that fails needs to know why.
 *
 * Suppressed on `/auth`, where the user is already signing in.
 */
export interface SessionExpiredBannerState {
  raised: boolean;
  dismissed: boolean;
  dismiss: () => void;
  signInAgain: () => Promise<void>;
}

export function useSessionExpiredBanner(): SessionExpiredBannerState {
  const isExpired = useIsSessionExpired();
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(
    () =>
      appEvents.on('session-expired', () => {
        sessionState.setExpired(true);
        setDismissed(false);
      }),
    []
  );

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(event => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        sessionState.setExpired(false);
        setDismissed(false);
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const signInAgain = async () => {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    sessionState.setExpired(false);
    setDismissed(false);
    try {
      await supabase.auth.signOut();
    } catch (err) {
      // Expected: the session is already invalid - that is the case here.
      console.error('signOut during re-login failed (expected):', err);
    }
    navigate(`/auth?mode=login&return=${returnTo}`);
  };

  return {
    raised: isExpired && !location.pathname.startsWith('/auth'),
    dismissed,
    dismiss: () => setDismissed(true),
    signInAgain,
  };
}
